<template>
  <div class="login-page flex min-h-screen w-full items-center justify-center px-4">
    <div class="w-full max-w-[450px] text-center">
      <img class="mx-auto mb-6 block" height="40" src="@/assets/logo_black.png" alt="Periscope" />

      <p v-if="route.query?.reason === 'token_expired'" class="mb-8 text-xl leading-8 tracking-wide">
        Din session har gått ut p.g.a. inaktivitet.
        <br />
        Logga in igen.
      </p>

      <p v-if="error" class="error-text mb-4">{{ error }}</p>

      <div v-if="selectedOrganisationValue" class="flex justify-between items-center p-2">
        Vald organisation: {{ selectedOrganisationValue }}
        <AppButton @click="onChangeOrganisation"> Byt organisation </AppButton>
      </div>

      <AppCard v-if="!selectedOrganisationValue" class="w-full !rounded-xl !p-6 text-left">
        <h2 class="mb-3 text-lg font-medium">Välj organisation</h2>
        <select
          class="org-select h-11 w-full rounded-lg px-3 text-base"
          :value="selectedOrganisationValue"
          @change="onSelectOrganisation"
        >
          <option value="" disabled>Välj organisation</option>
          <option v-for="organisation in organisations" :key="organisation.value" :value="organisation.value">
            {{ organisation.label }}
          </option>
        </select>
      </AppCard>

      <BankIdLoginStart
        v-else
        :loading="isLoading"
        :bank-id-logo-src="bankIdLogo"
        bank-id-logo-alt="BankID"
        button-text="Logga in med BankID"
        @start="onStartLogin"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { AppButton, AppCard, BankIdLoginStart } from '@staff/ui'

import bankIdLogo from '@/assets/bankid.svg'
import { newAnonymousToken, startBankIDAuth } from '@/services/auth'
import { useOrganisation } from '@staff/ui'

const router = useRouter()
const route = useRoute()

const isLoading = ref(false)
const error = ref('')

interface OrganisationOption {
  label: string
  value: string
}

const organisations: OrganisationOption[] = [
  'Hypoteket',
  'Hypoteket personallån',
  'ICA-banken',
  'ICA-banken personallån',
  'IKANO-bank',
  'IKANO-bank personallån',
  'Lån & Spar Bank',
  'Lån & Spar Bank personallån',
  'Sparbanken Syd',
  'Sparbanken Syd personallån',
  'Söderberg & Partners',
  'Söderberg & Partners personallån',
  'Ålandsbanken',
  'Ålandsbanken personallån',
  'Omni-distributor',
].flatMap((organisationName) => [{ label: organisationName, value: organisationName }])

const { setOrganisation, clearOrganisation, getLastOrganisation } = useOrganisation()
const selectedOrganisationValue = ref<string | null>(getLastOrganisation())

const onSelectOrganisation = (event: Event) => {
  const target = event.target as HTMLSelectElement
  const organisation = target.value

  if (!organisation) {
    return
  }
  selectedOrganisationValue.value = organisation
}

const onChangeOrganisation = () => {
  selectedOrganisationValue.value = null
  clearOrganisation()
}

const onStartLogin = async () => {
  const organisation = selectedOrganisationValue.value

  if (!organisation) {
    error.value = 'Välj organisation först.'
    return
  }

  if (isLoading.value) {
    return
  }

  setOrganisation(organisation)

  isLoading.value = true
  error.value = ''

  try {
    await newAnonymousToken(organisation)

    const response = await startBankIDAuth(organisation)

    if (!response.orderRef) {
      throw new Error('Missing order ref')
    }

    await router.push({
      name: 'auth',
      params: { orderRef: response.orderRef },
      query: {
        at: response.autoStartToken,
        qr: response.qrData,
        de: 'mobile',
        re: route.query.redirect?.toString() ?? '/ansokningar',
      },
    })
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Ett fel uppstod. Försök igen.'
  } finally {
    isLoading.value = false
  }
}
</script>

<style scoped lang="scss">
.login-page {
  background-color: var(--color-canvas-subtle);
}

.org-select {
  border: 1px solid var(--color-border-default);
  background-color: var(--color-canvas-default);
}
</style>
